import { PermissionSetModel } from "client/Api/common";
import { environmentHttpClient } from "client/Api/httpClient";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import { CommunityViewModel } from "./communityPosts";
import { UserIdentityModel } from "../UserProfile/service";
import { ImageFocalPointModel } from "../Hub/common";
import { UploadedFileInfoViewModel } from "../common";

export enum CommunityKind {
	Custom = 1,
	AllCompany = 2
};

export enum CommunityVisibility {
	Public = 1,
	Private = 2,
	Hidden = 3
}

export type CommunityMemberChanges = {
	added: readonly number[];
	removed: readonly number[];
};

export type CommunityAuthorPermissionsUnionModel = {
	everyoneWhoHasAccess: true;
} | {
	everyoneWhoHasAccess: false;
	specificSet: PermissionSetModel;
};

export type CommunityCoverImageSaveModel = {
	file: OptionalModel<Base64FileUriUploadModel>;
	focalPoint: ImageFocalPointModel;
};

export type SaveCommunityModel = {
	name: string;
	description: string;
	visibility: CommunityVisibility;
	image: CommunityCoverImageSaveModel | null;
	authors: CommunityAuthorPermissionsUnionModel;
	owners: PermissionSetModel;
	memberChanges: CommunityMemberChanges;
};

export type CreateCommunityResult = {
	id: number;
};

export type CommunityCoverImageViewModel = {
	file: UploadedFileInfoViewModel;
	focalPoint: ImageFocalPointModel;
};

export type EditCommunityViewModel = {
	kind: CommunityKind;
	name: string;
	description: string;
	visibility: CommunityVisibility;
	image: CommunityCoverImageViewModel | null;
	authors: CommunityAuthorPermissionsUnionModel;
	owners: PermissionSetModel;
	members: readonly number[];
};

export type CommunityImageRestrictions = {
	supportedImageTypes: readonly string[];
	maxFileSize: number;
};

export type EditCommunityPageViewModel = {
	community: EditCommunityViewModel;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	imageRestrictions: CommunityImageRestrictions;
};

export function getNewModel(signal?: AbortSignal): Promise<EditCommunityPageViewModel> {
	return environmentHttpClient.get<EditCommunityPageViewModel>(
		"UiApi/Communities/New",
		undefined,
		{ signal }
	);
}

export function getEditModel(id: number, signal?: AbortSignal): Promise<EditCommunityPageViewModel> {
	return environmentHttpClient.get<EditCommunityPageViewModel>(
		`UiApi/Communities/${id}/Edit`,
		undefined,
		{ signal }
	);
}

export function getDirectUserMembersOfUserGroups(
	userGroupIds: readonly number[],
	signal?: AbortSignal
): Promise<readonly UserGridItem[]> {
	return environmentHttpClient.post<readonly UserGridItem[]>(
		"UiApi/Communities/DirectUserMembersOfUserGroups",
		userGroupIds,
		{ signal }
	);
}

export function create(
	model: SaveCommunityModel,
	signal?: AbortSignal
): Promise<CreateCommunityResult> {
	return environmentHttpClient.post<CreateCommunityResult>(
		"UiApi/Communities/New",
		model,
		{ signal }
	);
}

export function edit(
	model: SaveCommunityModel,
	id: number,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`/UiApi/Communities/${id}/Edit`,
		model,
		{ signal }
	);
}

export enum CommunityMembershipStatus {
	NotMember = 1,
	Member = 2,
	RequestPending = 3
}

export type CommunityUserInfo = {
	isNew: boolean;
	membershipStatus: CommunityMembershipStatus;
	canAccess: boolean;
	canAccessWithoutMembership: boolean;
	canEdit: boolean;
	canDelete: boolean;
};

export type CommunityDiscoveryListItem = {
	id: number;
	kind: CommunityKind;
	name: string;
	description: string | null;
	visibility: CommunityVisibility;
	image: CommunityCoverImageViewModel | null;
	membersCount: number;
	userInfo: CommunityUserInfo;
};

export type CommunityDiscoverPageModel = {
	canCreateNewCommunity: boolean;
	communities: readonly CommunityDiscoveryListItem[];
};

export function discoverCommunities(signal?: AbortSignal): Promise<CommunityDiscoverPageModel> {
	return environmentHttpClient.get<CommunityDiscoverPageModel>(
		"UiApi/Communities/Discover",
		{ signal }
	);
}

export function joinCommunity(id: number, signal?: AbortSignal): Promise<number> {
	return environmentHttpClient.put<number>(
		`/UiApi/Communities/${id}/Join`,
		undefined,
		{ signal }
	);
}

export type LeaveCommunityResult = {
	isStillVisible: true;
	membersCount: number;
} | {
	isStillVisible: false;
};

export function leaveCommunity(id: number, signal?: AbortSignal): Promise<LeaveCommunityResult> {
	return environmentHttpClient.put<LeaveCommunityResult>(
		`/UiApi/Communities/${id}/Leave`,
		undefined,
		{ signal }
	);
}

export enum RequestToJoinCommunityResult {
	Created = 1,
	AlreadyPending = 2,
	AlreadyMember = 3
}

export function requestToJoinCommunity(
	id: number,
	signal?: AbortSignal
): Promise<RequestToJoinCommunityResult> {
	return environmentHttpClient.post<RequestToJoinCommunityResult>(
		`/UiApi/Communities/${id}/MembershipRequests`,
		undefined,
		{ signal }
	);
}

export function markCommunityAsSeen(id: number, signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.post<void>(
		`/UiApi/Communities/${id}/Seen`,
		undefined,
		{ signal }
	);
}

export function registerFollowCommunity(id: number, signal?: AbortSignal): Promise<number> {
	return environmentHttpClient.post<number>(
		`/UiApi/Communities/${id}/Subscription`,
		undefined,
		{ signal }
	);
}

export function unregisterFollowCommunity(id: number, signal?: AbortSignal): Promise<number> {
	return environmentHttpClient.delete<number>(
		`/UiApi/Communities/${id}/Subscription`,
		undefined,
		{ signal }
	);
}

export function deleteCommunity(id: number, signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.delete<void>(
		`/UiApi/Communities/${id}`,
		undefined,
		{ signal }
	);
}

export function listCommunities(
	signal?: AbortSignal
): Promise<readonly CommunityViewModel[]> {
	return environmentHttpClient.get<readonly CommunityViewModel[]>(
		`/UiApi/Communities`,
		undefined,
		{ signal }
	);
}

export type ListCommunityMembersRequestModel = {
	skip: number;
	take: number;
};

export function listCommunityMembers(
	communityId: number,
	request: ListCommunityMembersRequestModel,
	signal: AbortSignal
): Promise<LazyLoadPageResult<UserIdentityModel>> {
	return environmentHttpClient.get<LazyLoadPageResult<UserIdentityModel>>(
		`UiApi/Communities/${communityId}/Members`,
		request,
		{ signal }
	);
}
